import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const L = {
  en: { checking: "Confirming your subscription…", ok: "You're in!", okBody: "Your 7-day trial has started. Let's build your twin.", go: "Go to my dashboard", goClub: "Go to my club", slow: "Still confirming with Stripe… you can continue, we'll sync in the background.", fail: "Payment not confirmed", failBody: "Your session expired or was cancelled. No charge was made.", retry: "Back to plans", cancelTitle: "Checkout cancelled", cancelBody: "No worries — nothing was charged. Pick a plan whenever you're ready." },
  fr: { checking: "Confirmation de ton abonnement…", ok: "C'est parti !", okBody: "Ton essai de 7 jours a démarré. Construisons ton jumeau.", go: "Aller au tableau de bord", goClub: "Aller à mon club", slow: "Toujours en confirmation avec Stripe… tu peux continuer, on synchronise en arrière-plan.", fail: "Paiement non confirmé", failBody: "Ta session a expiré ou a été annulée. Aucun prélèvement.", retry: "Retour aux plans", cancelTitle: "Paiement annulé", cancelBody: "Pas de souci — rien n'a été débité. Choisis un plan quand tu veux." },
};

export function PaymentSuccess() {
  const { lang } = useLang();
  const t = L[lang] || L.en;
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { user, refreshBilling } = useAuth();
  const [state, setState] = useState("checking");
  const tries = useRef(0);

  useEffect(() => {
    const sid = sp.get("session_id");
    if (!sid) return setState("fail");
    let stop = false;
    const poll = async () => {
      if (stop) return;
      tries.current += 1;
      try {
        const r = await api.get(`/billing/status/${sid}`);
        if (r.data.payment_status === "paid") { await refreshBilling(); setState("ok"); return; }
        if (["expired", "failed"].includes(r.data.payment_status)) { setState("fail"); return; }
      } catch { /* retry */ }
      if (tries.current >= 8) { setState("slow"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sp, refreshBilling]);

  const dest = user?.role === "club" ? "/club" : "/dashboard";
  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4" data-testid="payment-success-page">
      <div className="max-w-md w-full rounded-2xl border border-[#1E2638] bg-[#121721] p-10 text-center">
        {state === "checking" && <><Loader2 className="w-12 h-12 mx-auto text-[#00F076] animate-spin" /><div className="mt-4 text-slate-300" data-testid="payment-checking">{t.checking}</div></>}
        {(state === "ok" || state === "slow") && (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto text-[#00F076]" />
            <h1 className="font-display font-black uppercase text-3xl mt-4" data-testid="payment-ok">{t.ok}</h1>
            <p className="text-slate-400 mt-2 text-sm">{state === "ok" ? t.okBody : t.slow}</p>
            <Button onClick={() => nav(dest)} data-testid="payment-continue-btn" className="mt-6 w-full bg-[#00F076] hover:bg-[#00C963] text-black font-bold uppercase tracking-wider">{user?.role === "club" ? t.goClub : t.go}</Button>
          </>
        )}
        {state === "fail" && (
          <>
            <XCircle className="w-14 h-14 mx-auto text-[#FF2E54]" />
            <h1 className="font-display font-black uppercase text-3xl mt-4" data-testid="payment-fail">{t.fail}</h1>
            <p className="text-slate-400 mt-2 text-sm">{t.failBody}</p>
            <Link to="/pricing"><Button className="mt-6 w-full bg-white/10 text-white font-bold uppercase">{t.retry}</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const { lang } = useLang();
  const t = L[lang] || L.en;
  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4" data-testid="payment-cancel-page">
      <div className="max-w-md w-full rounded-2xl border border-[#1E2638] bg-[#121721] p-10 text-center">
        <XCircle className="w-14 h-14 mx-auto text-amber-400" />
        <h1 className="font-display font-black uppercase text-3xl mt-4">{t.cancelTitle}</h1>
        <p className="text-slate-400 mt-2 text-sm">{t.cancelBody}</p>
        <Link to="/pricing"><Button data-testid="payment-cancel-back" className="mt-6 w-full bg-[#00F076] hover:bg-[#00C963] text-black font-bold uppercase">{t.retry}</Button></Link>
      </div>
    </div>
  );
}
